"use client";

import { useEffect, useState } from "react";
import {
  Check,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  FileText,
  Globe,
  History,
  Loader2,
  MessageSquare,
  RotateCcw,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { apiRequest } from "@/lib/api";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";

interface VersionItem {
  id: string;
  document_id: string;
  version_no: number;
  title: string;
  plain_text: string;
  commit_message: string;
  created_at: string;
}

export function VersionHistoryModal({
  open,
  onClose,
  documentId,
  onRestored,
}: {
  open: boolean;
  onClose: () => void;
  documentId?: string;
  onRestored: (restoredTitle: string, restoredContent: string) => void;
}) {
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<VersionItem | null>(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!open || !documentId) return;
    setLoading(true);
    apiRequest<VersionItem[]>(`/v1/documents/${documentId}/versions`)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setVersions(list);
        if (list.length > 0) setSelectedVersion(list[0]);
      })
      .catch(() => {
        toast.error("No se pudo cargar el historial de versiones");
      })
      .finally(() => setLoading(false));
  }, [open, documentId]);

  if (!open) return null;

  const handleRestore = async (v: VersionItem) => {
    if (!documentId) return;
    setRestoring(true);
    try {
      const restored = await apiRequest<VersionItem>(
        `/v1/documents/${documentId}/versions/${v.version_no}/restore`,
        { method: "POST" },
      );
      onRestored(restored.title, restored.plain_text || "");
      toast.success("Versión restaurada", `Se restauró la versión v${v.version_no}.`);
      onClose();
    } catch (err) {
      toast.error(
        "No se pudo restaurar la versión",
        err instanceof Error ? err.message : "",
      );
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-background/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[82vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-border/50 flex items-center justify-between bg-secondary/20">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">
              Historial de versiones
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!documentId ? (
          <div className="p-8 text-center text-xs text-muted-foreground">
            Esta nota aún se está sincronizando con el servidor.
          </div>
        ) : loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : versions.length === 0 ? (
          <div className="p-8 text-center text-xs text-muted-foreground">
            No hay versiones registradas para este documento.
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 md:grid-cols-5 min-h-0 overflow-hidden">
            <div className="md:col-span-2 border-r border-border/40 overflow-y-auto custom-scrollbar divide-y divide-border/20">
              {versions.map((v, idx) => {
                const active = selectedVersion?.id === v.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVersion(v)}
                    className={cn(
                      "w-full text-left px-4 py-3 transition-colors flex flex-col gap-1",
                      active ? "bg-primary/10" : "hover:bg-secondary/40",
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                        v{v.version_no}
                        {idx === 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-bold uppercase">
                            Actual
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" />
                        {new Date(v.created_at).toLocaleString("es-MX", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {v.commit_message || "Actualización automática"}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="md:col-span-3 flex flex-col min-h-0 bg-background/50">
              {selectedVersion ? (
                <>
                  <div className="px-5 py-3 border-b border-border/30 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-foreground">
                        {selectedVersion.title} (v{selectedVersion.version_no})
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(selectedVersion.created_at).toLocaleString("es-MX")}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRestore(selectedVersion)}
                      disabled={restoring || selectedVersion.id === versions[0]?.id}
                      className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-40 transition-all"
                    >
                      {restoring ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5" />
                      )}
                      Restaurar versión
                    </button>
                  </div>
                  <div className="flex-1 p-5 overflow-y-auto custom-scrollbar">
                    <pre className="text-xs font-mono whitespace-pre-wrap text-foreground/85 leading-relaxed">
                      {selectedVersion.plain_text || "(Documento vacío en esta versión)"}
                    </pre>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface CommentItem {
  id: string;
  author_id: string;
  author_name: string;
  body: string;
  created_at: string;
}

interface CommentThreadItem {
  thread_id: string;
  status: string;
  comments: CommentItem[];
}

export function DocumentCommentsModal({
  open,
  onClose,
  documentId,
}: {
  open: boolean;
  onClose: () => void;
  documentId?: string;
}) {
  const [threads, setThreads] = useState<CommentThreadItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = async () => {
    if (!documentId) return;
    setLoading(true);
    try {
      const res = await apiRequest<{ threads: CommentThreadItem[] }>(
        `/v1/comments/document/${documentId}`,
      );
      setThreads(res?.threads || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && documentId) {
      fetchComments();
    }
  }, [open, documentId]);

  if (!open) return null;

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentId || !newComment.trim()) return;
    setSubmitting(true);
    try {
      const thread = await apiRequest<{ id: string }>("/v1/comments/threads", {
        method: "POST",
        body: JSON.stringify({
          document_id: documentId,
          anchor: { type: "document" },
        }),
      });
      await apiRequest("/v1/comments", {
        method: "POST",
        body: JSON.stringify({
          thread_id: thread.id,
          body: newComment.trim(),
        }),
      });
      setNewComment("");
      await fetchComments();
      toast.success("Comentario publicado");
    } catch (err) {
      toast.error(
        "No se pudo publicar el comentario",
        err instanceof Error ? err.message : "",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (threadId: string) => {
    try {
      await apiRequest(`/v1/comments/threads/${threadId}/resolve`, {
        method: "POST",
      });
      await fetchComments();
      toast.success("Hilo marcado como resuelto");
    } catch {
      toast.error("No se pudo resolver el hilo");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-background/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-border/50 flex items-center justify-between bg-secondary/20">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">
              Comentarios de la nota
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3 custom-scrollbar">
          {!documentId ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              Guarda o espera a que la nota se sincronice para comentar.
            </p>
          ) : loading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : threads.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">
              Aún no hay comentarios en esta nota. Sé el primero en dejar feedback o mencionar a alguien con <code className="px-1 py-0.5 bg-secondary rounded">@usuario</code>.
            </p>
          ) : (
            threads.map((t) => (
              <div
                key={t.thread_id}
                className={cn(
                  "p-3.5 rounded-xl border transition-all",
                  t.status === "resolved"
                    ? "bg-secondary/10 border-border/30 opacity-60"
                    : "bg-background border-border/60",
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-foreground">
                    {t.comments[0]?.author_name || "Usuario"}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground">
                      {t.comments[0]?.created_at
                        ? new Date(t.comments[0].created_at).toLocaleString("es-MX", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </span>
                    {t.status !== "resolved" ? (
                      <button
                        onClick={() => handleResolve(t.thread_id)}
                        className="text-[10px] text-emerald-500 hover:underline flex items-center gap-1"
                        title="Marcar como resuelto"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Resolver
                      </button>
                    ) : (
                      <span className="text-[10px] text-emerald-500 font-medium">
                        Resuelto
                      </span>
                    )}
                  </div>
                </div>
                {t.comments.map((c) => (
                  <p key={c.id} className="text-xs text-foreground/90 leading-relaxed">
                    {c.body}
                  </p>
                ))}
              </div>
            ))
          )}
        </div>

        {documentId && (
          <form
            onSubmit={handleAddComment}
            className="p-3.5 border-t border-border/50 bg-secondary/10 flex items-center gap-2"
          >
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Escribe un comentario o menciona con @usuario..."
              className="flex-1 h-9 px-3 bg-background border border-input rounded-lg text-xs text-foreground focus:outline-none focus:border-primary"
            />
            <button
              type="submit"
              disabled={submitting || !newComment.trim()}
              className="h-9 px-3.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
            >
              {submitting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              Comentar
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

interface PublicationItem {
  id: string;
  resource_type: string;
  resource_id: string;
  slug: string;
  visibility: string;
}

export function ShareDocumentModal({
  open,
  onClose,
  documentId,
  documentTitle,
  documentContent,
}: {
  open: boolean;
  onClose: () => void;
  documentId?: string;
  documentTitle: string;
  documentContent: string;
}) {
  const [existingPub, setExistingPub] = useState<PublicationItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !documentId) return;
    setLoading(true);
    apiRequest<PublicationItem[]>("/v1/publications")
      .then((list) => {
        const found = (list || []).find(
          (p) => p.resource_type === "document" && p.resource_id === documentId,
        );
        setExistingPub(found || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, documentId]);

  if (!open) return null;

  const cleanTitle = documentTitle.replace(/\.md$/i, "");
  const publicUrl =
    existingPub && typeof window !== "undefined"
      ? `${window.location.origin}/public/${existingPub.slug}`
      : "";

  const handlePublish = async () => {
    if (!documentId) return;
    setPublishing(true);
    try {
      const baseSlug = cleanTitle
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 48);
      const slug = `${baseSlug || "nota"}-${documentId.slice(0, 6)}`;
      const created = await apiRequest<PublicationItem>("/v1/publications", {
        method: "POST",
        body: JSON.stringify({
          resource_type: "document",
          resource_id: documentId,
          slug,
          visibility: "public",
          indexable: true,
        }),
      });
      setExistingPub(created);
      toast.success("Nota publicada en la web", "Ya puedes compartir el enlace público.");
    } catch (err) {
      toast.error(
        "No se pudo publicar la nota",
        err instanceof Error ? err.message : "",
      );
    } finally {
      setPublishing(false);
    }
  };

  const handleCopyUrl = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success("Enlace copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveAsTemplate = async () => {
    setSavingTemplate(true);
    try {
      await apiRequest("/v1/templates", {
        method: "POST",
        body: JSON.stringify({
          name: cleanTitle,
          description: `Plantilla creada a partir de "${cleanTitle}"`,
          scope: "global",
          content_json: {
            markdown: documentContent,
          },
        }),
      });
      toast.success(
        "Guardada como plantilla",
        `"${cleanTitle}" ahora está disponible en la galería de Plantillas.`,
      );
    } catch (err) {
      toast.error(
        "No se pudo guardar como plantilla",
        err instanceof Error ? err.message : "",
      );
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-background/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-border/50 flex items-center justify-between bg-secondary/20">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">
              Compartir y reutilizar nota
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Public web link section */}
          <div className="space-y-2.5">
            <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-primary" />
              Enlace público de solo lectura
            </h4>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Publica una instantánea de esta nota para que cualquier persona con el enlace pueda leerla sin iniciar sesión.
            </p>

            {loading ? (
              <div className="py-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Verificando estado de publicación...
              </div>
            ) : existingPub ? (
              <div className="space-y-2 pt-1">
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    readOnly
                    value={publicUrl}
                    className="flex-1 h-8 px-2.5 bg-secondary/40 border border-border rounded-lg text-xs font-mono text-foreground"
                  />
                  <button
                    onClick={handleCopyUrl}
                    className="h-8 px-2.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 flex items-center gap-1 shrink-0"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copiado" : "Copiar"}
                  </button>
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="h-8 px-2.5 border border-border rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 flex items-center"
                    title="Abrir página pública"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            ) : (
              <button
                onClick={handlePublish}
                disabled={publishing || !documentId}
                className="w-full h-9 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {publishing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Globe className="w-3.5 h-3.5" />
                )}
                Generar enlace público
              </button>
            )}
          </div>

          <div className="border-t border-border/40 pt-4 space-y-2.5">
            <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-primary" />
              Guardar como plantilla
            </h4>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Convierte el contenido actual de esta nota en una plantilla reutilizable para todo tu equipo.
            </p>
            <button
              onClick={handleSaveAsTemplate}
              disabled={savingTemplate}
              className="w-full h-9 bg-secondary hover:bg-secondary/80 text-foreground border border-border/60 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
            >
              {savingTemplate ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 text-primary" />
              )}
              Guardar nota en Galería de Plantillas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
